'use client';
import { objectKeys } from '@s7/utils';
import { deriveBaseShades } from './derivations/deriveBaseShades/deriveBaseShades.js';
import { deriveTextShades } from './derivations/deriveTextShades/deriveTextShades.js';
import { deriveBackgroundShades } from './derivations/deriveBackgroundShades/deriveBackgroundShades.js';
import { rgba } from '../../../../fns/rgba/rgba.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _create_for_of_iterator_helper_loose(o, allowArrayLike) {
    var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
    if (it) return (it = it.call(o)).next.bind(it);
    if (Array.isArray(o) || (it = _unsupported_iterable_to_array(o)) || allowArrayLike && o && typeof o.length === "number") {
        if (it) o = it;
        var i = 0;
        return function() {
            if (i >= o.length) {
                return {
                    done: true
                };
            }
            return {
                done: false,
                value: o[i++]
            };
        };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
var getColorShades = function(color, overrides) {
    if (overrides === void 0) overrides = {};
    var _objectKeys = objectKeys(color), colorName = _objectKeys[0];
    if (!color[colorName]) {
        return {};
    }
    var shades = color[colorName];
    var overrideKeys = objectKeys(overrides);
    var override = {};
    for(var _iterator = _create_for_of_iterator_helper_loose(overrideKeys), _step; !(_step = _iterator()).done;){
        var shadeKey = _step.value;
        var _overrides_shadeKey = overrides[shadeKey], dayShade = _overrides_shadeKey[0], nightShade = _overrides_shadeKey[1];
        var _obj;
        override = _extends({}, override, (_obj = {}, _obj[shadeKey] = {
            day: shades[dayShade],
            night: shades[nightShade]
        }, _obj));
    }
    var _obj1;
    return _obj1 = {}, _obj1[colorName] = _extends({}, deriveBaseShades(color[colorName]), override), _obj1;
};
var createPalette = function(colors, invariants) {
    var green = colors.green, blue = colors.blue, orange = colors.orange, red = colors.red, turquoise = colors.turquoise, violet = colors.violet, emerald = colors.emerald, yellow = colors.yellow, pink = colors.pink, brand = colors.brand;
    var neutral = invariants.neutral, white = invariants.white, black = invariants.black;
    var semanticGreen = green ? deriveBaseShades(green) : undefined;
    /*
     * Вычисление дополнительных семантических цветов здесь
     * позволяет избежать появления логики с множеством исключений,
     * размазанной по всем компонентам.
     * */ return _extends({}, semanticGreen && {
        primary: semanticGreen,
        success: semanticGreen,
        miles: semanticGreen
    }, blue && {
        info: _extends({}, deriveBaseShades(blue), {
            base: {
                day: blue[600],
                night: blue[600]
            }
        })
    }, orange && {
        attention: deriveBaseShades(orange)
    }, red && {
        disruption: deriveBaseShades(red)
    }, {
        text: _extends({}, deriveTextShades(neutral, white), brand && {
            brand: {
                day: brand[600],
                night: brand[600]
            }
        }),
        background: deriveBackgroundShades(neutral, white)
    }, neutral && {
        border: _extends({
            card: {
                day: neutral[40],
                night: neutral[900]
            },
            divider: {
                day: neutral[50],
                night: neutral[700]
            },
            element: {
                day: neutral[60],
                night: neutral[300]
            }
        }, brand && {
            brandSecondary: {
                day: brand[50],
                night: brand[950]
            },
            brand: {
                day: brand[600],
                night: brand[600]
            }
        }),
        tooltip: {
            background: {
                day: neutral[500],
                night: neutral[40]
            }
        },
        cookie: {
            background: {
                day: rgba(neutral[900], 0.92),
                night: rgba(neutral[30], 0.92)
            }
        },
        snackbar: {
            background: {
                day: neutral[900],
                night: neutral[30]
            }
        },
        modal: {
            background: {
                day: neutral[30],
                night: neutral[920]
            }
        }
    }, neutral && {
        button: _extends({
            secondary: {
                day: neutral[40],
                night: neutral[700]
            },
            translucent: {
                day: rgba(neutral[40], 0.08),
                night: rgba(neutral[700], 0.08)
            }
        }, white && {
            white: {
                day: white[100],
                night: rgba("#CBD9F61A", 0.1)
            }
        })
    }, {
        tabsnav: {
            background: {
                day: "#F8F9FCD9",
                night: "#131724D9"
            },
            tabActive: {
                day: "#8CC52F",
                night: "#7CB124"
            }
        },
        mixture: _extends({
            light: {
                day: "#032058",
                night: "#A6BEEE"
            }
        }, neutral && {
            color: {
                day: neutral[900],
                night: "#000"
            }
        }, {
            dark: {
                day: "#18202D",
                night: "#F3F6FC"
            }
        }, neutral && white && {
            stronger: {
                day: white[100],
                night: neutral[920]
            }
        }),
        other: _extends({}, neutral && black && {
            shadow: {
                day: neutral[900],
                night: black[100]
            }
        }, {
            fade: {
                day: "#0000004d",
                night: "#00000099"
            }
        }, white && {
            fadeBrightness: {
                day: white[0],
                night: "#222a3726"
            }
        }),
        promo: {
            deepGreen: "#098D41",
            green: "#50982F",
            seagreen: "#3EC075",
            superSeagreen: "#11EC7B",
            lime: "#C4D600",
            superLime: "#B7ECB8",
            superPistachio: "#B7ECB8",
            superBlue: "#3D53EE",
            tabrizBlue: "#007F9D",
            aquamarine: "#36C0C0",
            aqua: "#8EDEE6",
            superAqua: "#16ECE1",
            darkViolet: "#801F7E",
            purple: "#512D6D",
            mediumPurple: "#614D94",
            darkSlate: "#444791",
            superPurple: "#444791",
            scarlet: "#A83160",
            brightRed: "#E4445C",
            salamon: "#E4445C",
            candyPink: "#E4445C",
            citrine: "#FF961A",
            sand: "#FFA766",
            sorbetYellow: "#F3E169",
            superYellow: "#F3E169"
        }
    }, getColorShades({
        blue: blue
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        green: green
    }), getColorShades({
        orange: orange
    }), getColorShades({
        red: red
    }), getColorShades({
        turquoise: turquoise
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        violet: violet
    }, {
        base: [
            600,
            600
        ]
    }), getColorShades({
        pink: pink
    }), getColorShades({
        emerald: emerald
    }), getColorShades({
        yellow: yellow
    }));
};

export { createPalette };
